import Icon from '@/components/icons/Icon';
import { classnames, getUserInitials } from '@/helpers';
import { useAppSelector } from '@/hooks';
import useMobileView from '@/hooks/useMobileView';
import { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import css from './Button.module.scss';
import { Button, ButtonIdentifier } from './Button.types';

type ButtonProps = {
  item: Button;
  isActive: boolean;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  onMouseDown?: () => void;
} & React.HTMLAttributes<HTMLButtonElement | HTMLAnchorElement>;

const getButtonClasses = (button: Button, isActive: boolean) => {
  const shared = sharedButtonClasses(button);

  switch (button.identifier) {
    case ButtonIdentifier.Profile:
      return twMerge(
        shared,
        'active:bg-bg_brand active:border-border_brand active:text-fg_alwaysLight',
        isActive && 'bg-bg_brand border-border_brand text-fg_alwaysLight',
      );
    default:
      return twMerge(
        shared,
        'active:bg-bg_info_bold active:border-border_focus active:text-fg_alwaysLight',
        isActive && 'bg-bg_info_bold border-border_focus text-fg_alwaysLight',
      );
  }
};

const sharedButtonClasses = (item: Button) =>
  classnames(
    'items-center rounded-full inline-flex justify-center gap-xs relative border-border_regular border text-fg_primary hover:bg-bg_secondary',
    'focus-within:ring-border_focus outline-none focus-within:ring-[3px]',
    item.breakpoint && `hidden ${item.breakpoint}`,
    item.label && 'py-xs px-md',
  );

export default function ({ item, isActive, ...props }: ButtonProps) {
  const user = useAppSelector((state) => state.users?.user);
  const userInitials = user && getUserInitials(user);
  const { isMobileView } = useMobileView();
  const isLink = item?.to;
  const iconProps = {
    variant: item.icon,
    size: 'sm',
    className: 'my-xxs',
    style: { width: 24, height: 24 },
    color: isActive ? 'fg_alwaysLight' : 'fg_primary',
  } as const;

  const buttonStyles: React.CSSProperties = {
    minWidth: isMobileView ? 48 : 52,
    height: isMobileView ? 48 : 52,
  };

  const classNames = twMerge(classnames(getButtonClasses(item, isActive), item.className, css.button));

  if (isLink) {
    return item.to.startsWith('/') ? (
      <Link id={item.id} to={item.to} className={classNames} style={buttonStyles} {...props}>
        {item.icon && <Icon {...iconProps} />}
        {item.label && <small className="text-m text-current">{item.label}</small>}
      </Link>
    ) : (
      <a id={item.id} href={item.to} className={classNames} style={buttonStyles} {...props}>
        {item.icon && <Icon {...iconProps} />}
        {item.label && <small className="text-m text-current">{item.label}</small>}
      </a>
    );
  }

  return (
    <button id={item.id} className={classNames} style={buttonStyles} {...props}>
      {item.icon && <Icon {...iconProps} />}
      {item.label && <small className="text-m text-current">{item.label}</small>}
      {item.identifier === ButtonIdentifier.Profile && (
        <div
          className={classnames(
            'bg-brand text-m text-fg_alwaysLight grid items-center rounded-full',
            css.profileInitials,
          )}>
          {userInitials}
        </div>
      )}
    </button>
  );
}
